import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GenericTable from "../GenericTable/GenericTable";
import config from "../../../config";
import { Box, CircularProgress, TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup, FormControlLabel, Radio } from "@mui/material";

const API_BASE_URL = `${config.API_BASE_URL_V1}/admin/chatbot-report`;

const formatValue = (value) =>
  value === null || value === undefined || value === "" ? "N/A" : value;

// ✅ Format date from ISO format (2025-12-02T00:00:00) to MM-DD-YYYY HH:MM:SS
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return "N/A";
  }
};

// Table Columns must match backend response keys
const ProblemReportColumns = [
  { id: "client_name", label: "Client Name", render: (row) => formatValue(row.client_name) },
  { id: "store_name", label: "Store Name", render: (row) => formatValue(row.store_name) },
  { id: "customer_name", label: "Customer Name", render: (row) => formatValue(row.customer_name) },
  { id: "created_at", label: "Created At", render: (row) => formatDate(row.created_at) },
  { id: "issue_reported", label: "Issue Reported", render: (row) => formatValue(row.issue_reported) }
];

function ProblemReportsList() {
  const navigate = useNavigate();

  // Table state
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Filters
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedStore, setSelectedStore] = useState("");

  // Dropdown options
  const [clients, setClients] = useState([]);
  const [stores, setStores] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);

  // Export states
  const [exportOpen, setExportOpen] = useState(false);
  const [exportType, setExportType] = useState("csv");

  // Loader
  const [loading, setLoading] = useState(false);

  // ✅ FETCH FILTER OPTIONS (Clients & Stores) - Only once on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setDropdownLoading(true);
      try {
        const res = await axios.get(
          `${config.API_BASE_URL_V1}/admin/chatbot-report/filter-options`
        );
        if (res.data.success) {
          setClients(res.data.data.clients || []);
          setStores(res.data.data.stores || []);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
      setDropdownLoading(false);
    };
    fetchFilterOptions();
  }, []);

  // ✅ UNIVERSAL FETCH: Server-side pagination with filters
  const fetchReports = useCallback(async (pageNum = page, perPageNum = perPage, filters = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pageNum,
        per_page: perPageNum,
        client_id: filters.client_id !== undefined ? filters.client_id : selectedClient,
        store_id: filters.store_id !== undefined ? filters.store_id : selectedStore,
      };

      // Remove undefined/empty values
      Object.keys(queryParams).forEach(
        (key) => !queryParams[key] && delete queryParams[key]
      );

      const response = await axios.get(API_BASE_URL, { params: queryParams });

      setRows(response.data.data || []);
      setTotal(response.data.pagination?.total_records || 0);

    } catch (error) {
      console.error("❌ API fetch error:", error);
      setRows([]);
    }

    setLoading(false);
  }, [page, perPage, selectedClient, selectedStore]);

  // ✅ AUTO-FETCH: When page or perPage changes (keeps current filters)
  useEffect(() => {
    fetchReports(page, perPage, {
      client_id: selectedClient,
      store_id: selectedStore,
    });
  }, [page, perPage]);

  // ✅ HANDLE SEARCH: Reset to page 1 and fetch with filters
  const handleSearch = () => {
    setPage(1);
    fetchReports(1, perPage, {
      client_id: selectedClient,
      store_id: selectedStore,
    });
  };

  // ✅ CLEAR FILTERS: Reset all and fetch fresh
  const handleClearFilters = () => {
    setSelectedClient("");
    setSelectedStore("");
    setPage(1);
    fetchReports(1, perPage, {
      client_id: "",
      store_id: "",
    });
  };

  // ✅ EXPORT: Get ALL filtered results (ignores pagination)
  const handleExportData = async () => {
    try {
      setLoading(true);
      let allData = [];
      let currentPage = 1;
      let hasMore = true;

      // ✅ Fetch ALL pages until no more data
      while (hasMore) {
        const res = await axios.get(API_BASE_URL, {
          params: {
            page: currentPage,
            per_page: 100, // Backend max limit is 100
            client_id: selectedClient || undefined,
            store_id: selectedStore || undefined,
          },
        });

        const pageData = res.data?.data || [];

        if (pageData.length === 0) {
          hasMore = false;
        } else {
          allData = [...allData, ...pageData];
          currentPage++;
        }
      }

      // Generate CSV
      const headers = [
        "Client Name",
        "Store Name",
        "Customer Name",
        "Created At",
        "Issue Reported",
      ];

      const csvContent = [
        headers.join(","),
        ...allData.map((row) =>
          [
            row.client_name,
            row.store_name,
            row.customer_name || "N/A",
            formatDate(row.created_at),
            `"${row.issue_reported || ""}"`,
          ].join(",")
        ),
      ].join("\n");

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `problem_reports_export_${new Date().getTime()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setExportOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div>
      {/* ✅ FILTER BAR - WHITE BACKGROUND */}
      <Box sx={{ mb: 0, p: 2, backgroundColor: "#ffffff", borderRadius: "8px 8px 0 0", display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", border: "1px solid #e0e0e0", borderBottom: "none" }}>
        <TextField
          select
          label="Client Name"
          size="small"
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={dropdownLoading}
        >
          <MenuItem value="">-- Select Client --</MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.id} value={client.id}>
              {client.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Store Name"
          size="small"
          value={selectedStore}
          onChange={(e) => setSelectedStore(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={dropdownLoading}
        >
          <MenuItem value="">-- Select Store --</MenuItem>
          {stores.map((store) => (
            <MenuItem key={store.id} value={store.id}>
              {store.name}
            </MenuItem>
          ))}
        </TextField>

        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          onClick={handleClearFilters}
          sx={{ height: "40px", borderColor: "#1976d2", color: "#1976d2", "&:hover": { backgroundColor: "#f5f5f5" } }}
        >
          Clear Filters
        </Button>

        <Button
          variant="contained"
          onClick={() => setExportOpen(true)}
          sx={{ height: "40px", backgroundColor: "#1976d2", color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}
        >
          Export
        </Button>
      </Box>

      {/* ✅ EXPORT DIALOG */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)}>
        <DialogTitle>Export Problem Reports</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            sx={{ marginTop: "16px" }}
          >
            <FormControlLabel value="csv" control={<Radio />} label="CSV" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)}>Cancel</Button>
          <Button onClick={handleExportData} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ TABLE WITH SERVER-SIDE PAGINATION */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <GenericTable
          title=""
          columns={ProblemReportColumns}
          rows={rows}
          showView={true}
          showEdit={false}
          showDelete={false}
          hideExport={true}
          isServerSide={true}
          currentPage={page - 1}
          rowsPerPage={perPage}
          totalCount={total}
          onPageChange={(_, newPage) => {
            setPage(newPage + 1);
          }}
          onRowsPerPageChange={(event) => {
            const newRowsPerPage = parseInt(event.target.value, 10);
            setPerPage(newRowsPerPage);
            setPage(1);
          }}
          onView={(row) => navigate(`/problem-reports/${row.chatbot_customer_feedback_id}`)}
        />
      )}
    </div>
  );
}

export default ProblemReportsList;
















// import React from "react";
// import { useNavigate } from "react-router-dom";
// import GenericTable from "../GenericTable/GenericTable";
// import ProblemReports from "../../../data/report_problems_success.json"; // JSON file

// const ProblemReportColumns = [
//   { id: "client_id", label: "Client ID" },
//   { id: "store_id", label: "Store ID" },
//   { id: "customer_name", label: "Customer Name" },
//   { id: "customer_id", label: "Customer ID" },
//   { id: "issue_reported", label: "Issue Reported" }
// ];

// function ProblemReportsList() {
//   const navigate = useNavigate();

//   // Rows come directly from JSON since they’re already flat
//   const rows = ProblemReports.data || [];

//   return (
//     <GenericTable
//       title="Problem Reports"
//       columns={ProblemReportColumns}
//       rows={rows}
//       showView={true}
//       showEdit={false}
//       showDelete={false}
//       dropdownFilters={["client_id", "store_id"]}
//       onView={(row) => {
//         // Example: Navigate to issue details page by customer_id
//         navigate(`/problem-reports/${row.customer_id}`);
//       }}
//     />
//   );
// }

// export default ProblemReportsList;
